import React, { useState, useEffect, useRef } from 'react'
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/useStores'
import UserHelper from '../../utilities/UserHelper'
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import CreateAliasUserModal from './CreateAliasUserModal'
import ToggleStatusModal from './ToggleStatusModal'
import { DEFAULT_ORG } from '../../stores/Organizations';
import useApiRequest from '../../hooks/useApiRequest.js';
import { useOktaAuth } from '@okta/okta-react'
import { 
  PERMISSION_ERROR_MESSAGE,
  PAGE_TITLE,
  PAGE_DESCRIPTION,
  ORGANIZATION_LABEL,
  TABLE_NAME_HEADER,
  TABLE_EMAIL_HEADER,
  TABLE_STATUS_HEADER,
  STATUS_ACTIVATED,
  STATUS_DEACTIVATED,
  NO_USERS_FOUND,
  LOADING_PERMISSIONS,
  LOADING_USERS_TEXT,
  CREATE_ALIAS_USER,
} from './constants'

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    width: '100%',
    maxWidth: '1200px',
    margin: '20px auto',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusLabel: {
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  alertContainer: {
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  permissionButtons: {
    padding: '16px',
    marginTop: '16px',
    width: '600px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  tableWrapper: {
    overflow: 'auto',
    maxHeight: '350px',
  },
  createButton: {
    margin: '0 10px',
    width: '120px',
    height: '50px',
    backgroundColor: '#CFD7DF',
    '&:hover': {
      backgroundColor: '#CFD7DF',
    },
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  titleSection: {
    flex: '1',
  },
  buttonSection: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const AliasUserManagement = observer(() => {
  const { oktaAuth } = useOktaAuth()
  const classes = useStyles();
  const { organizationStore, permissionsStore } = useStores()
  const [aliasUsers, setAliasUsers] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedHiMarleyUser, setSelectedHiMarleyUser] = useState(null)
  const [hiMarleyUsers, setHiMarleyUsers] = useState([])
  const [hiMarleyUsersPage, setHiMarleyUsersPage] = useState(1)
  const [hiMarleyUsersHasMore, setHiMarleyUsersHasMore] = useState(false)
  const [hasLoadedHiMarleyUsersOnce, setHasLoadedHiMarleyUsersOnce] = useState(false)
  const hiMarleyUsersSearchRef = useRef('')
  // Only the latest search/load-more may publish its results.
  const latestHiMarleyRequestId = useRef(0)
  const fetchedLoggedInUser = permissionsStore.loggedInUserFetched
  const hasAliasAccess = fetchedLoggedInUser && permissionsStore.loggedInUserPermissions.includes('ALIAS_OPERATOR_MGMT')


  // switching to the permissions view changes the selected organization to Hi Marley
  useEffect(() => {
    organizationStore.setSelected(DEFAULT_ORG)
    oktaAuth.token.getUserInfo().then((info)=> { 
      permissionsStore.getLoggedInUsersPermissions(info.email)
    })
  },[])

  const {
    execute: createAliasUser,
    loading: isLoadingCreateAliasUser,
    error: createAliasUserError,
    reset: resetCreateAliasUserError,
  } = useApiRequest(UserHelper.createAliasUser, {
    onSuccess: () => {
      setCreateModalOpen(false)
      return fetchAliasUsers(organizationStore.selectedOrgId)
    }
  })
  const {
    execute: fetchAliasUsers,
    loading: isLoadingAliasUsers,
    error: aliasUsersError,
  } = useApiRequest(UserHelper.getAliasUsers, {
    onSuccess: (data) => {
      setAliasUsers(data)
    }
  })

  useEffect(() => {
    if (hasAliasAccess && organizationStore.selectedOrgId && organizationStore.selectedOrgId !== DEFAULT_ORG) {
      fetchAliasUsers(organizationStore.selectedOrgId)
    }
  },[organizationStore.selectedOrgId, hasAliasAccess])

  const {
    execute: requestHiMarleyUsersPage,
    loading: isLoadingHiMarleyUsers,
    error: hiMarleyUsersError,
    reset: resetHiMarleyUsersError,
  } = useApiRequest(UserHelper.fetchHiMarleyUsersPage)

  const searchHiMarleyUsers = async (search = '') => {
    const requestId = ++latestHiMarleyRequestId.current
    const response = await requestHiMarleyUsersPage(search, 1)
    if (!response || requestId !== latestHiMarleyRequestId.current) return
    hiMarleyUsersSearchRef.current = search
    setHiMarleyUsers(response.users)
    setHiMarleyUsersHasMore(response.hasMore)
    setHiMarleyUsersPage(1)
    if (!hasLoadedHiMarleyUsersOnce) setHasLoadedHiMarleyUsersOnce(true)
  }

  const loadMoreHiMarleyUsers = async () => {
    if (!hiMarleyUsersHasMore) return
    const requestId = ++latestHiMarleyRequestId.current
    const searchAtRequest = hiMarleyUsersSearchRef.current
    const nextPage = hiMarleyUsersPage + 1
    const response = await requestHiMarleyUsersPage(searchAtRequest, nextPage)
    // Drop if a newer request or search superseded this one.
    if (!response || requestId !== latestHiMarleyRequestId.current || searchAtRequest !== hiMarleyUsersSearchRef.current) return
    setHiMarleyUsers(prevUsers => [...prevUsers, ...response.users])
    setHiMarleyUsersHasMore(response.hasMore)
    setHiMarleyUsersPage(nextPage)
  }

  const {
    execute: updateAliasUserStatus,
    loading: isLoadingUpdateAliasUserStatus,
    error: updateAliasUserStatusError,
    reset: resetUpdateAliasUserStatus,
  } = useApiRequest(async (aliasId, orgId, deActivate) => {
    const apiFn = deActivate
      ? UserHelper.deactivateAliasUser
      : UserHelper.reactivateAliasUser;
    return await apiFn(aliasId, orgId);
  }, {
    onSuccess: (data) => {
      setIsDialogOpen(false)
      setSelectedUser(null)
      setAliasUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === data.id
            ? { ...user, active: data.active }
            : user
        )
      )
    },
  })

  const handleStatusToggle = (user) => {
    setSelectedUser(user)
    setIsDialogOpen(true)
  }

  const handleConfirmStatusChange = async () => {
    if (!selectedUser) return
    const { active, id } = selectedUser
    const deActivate = active
    await updateAliasUserStatus(id, organizationStore.selectedOrgId, deActivate)
  }

  const handleDropdownSelection = (email) => {
    const selectedUser = hiMarleyUsers.find(user => user.email === email);
    setSelectedHiMarleyUser(selectedUser)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setSelectedUser(null)
    resetUpdateAliasUserStatus()
  }

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true)
    searchHiMarleyUsers('')
    resetCreateAliasUserError()
    resetHiMarleyUsersError()
    setSelectedHiMarleyUser(null)
  }

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false)
  }

  const handleSubmitCreateUser = async () => createAliasUser(selectedHiMarleyUser?.email, organizationStore.selectedOrgId)

  const renderAliasUsersTable = (aliasUsers, isLoadingAliasUsers, aliasUsersError) => {
    if (isLoadingAliasUsers) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: '16px' }}>
            {LOADING_USERS_TEXT}
          </Typography>
        </div>
      )
    }

    if (aliasUsersError) {
      return (
        <div className={classes.alertContainer}>
          <Alert severity="error">{aliasUsersError}</Alert>
        </div>
      );
    }

    if (aliasUsers) {
      return (
        <div className={classes.tableWrapper}>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{TABLE_NAME_HEADER}</TableCell>
                  <TableCell>{TABLE_EMAIL_HEADER}</TableCell>
                  <TableCell>{TABLE_STATUS_HEADER}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(aliasUsers) && aliasUsers.length > 0 ? (
                  aliasUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`row-${user.id}`}>
                      <TableCell>
                        {`${user?.firstName || ''} ${user?.lastName || ''}`}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <div className={classes.statusCell}>
                          <span className={classes.statusLabel}>
                            {user.active ? STATUS_ACTIVATED : STATUS_DEACTIVATED}
                          </span>
                          <Switch
                            checked={user.active}
                            onChange={() => handleStatusToggle(user)}
                            color="primary"
                            size="small"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {NO_USERS_FOUND}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      );
    }
    return <div></div>
  }

  if (!fetchedLoggedInUser) {  
    return (
      <div className={classes.pageContainer}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: '16px' }}>
            {LOADING_PERMISSIONS}
          </Typography>
        </div>
      </div>
    )
  } else if (!hasAliasAccess) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.alertContainer}>
          <Alert severity="error">{PERMISSION_ERROR_MESSAGE}</Alert>
        </div>
      </div>
    )
  }
  return (
    <div className={classes.pageContainer}>
      <div className={classes.headerContainer}>
        <div className={classes.titleSection}>
          <Typography variant="h4" component="h1" gutterBottom>
            {PAGE_TITLE}
          </Typography>
          <Typography variant="body1" className={classes.description}>
            {PAGE_DESCRIPTION}
          </Typography>
        </div>
        {organizationStore.selectedOrgId && (<div className={classes.buttonSection}>
          <Button
            variant="contained"
            color="default"
            size="small"
            className={classes.createButton}
            onClick={handleOpenCreateModal}
          >
            {CREATE_ALIAS_USER}
          </Button>
        </div>)}
      </div>
      
      <Typography variant="h6" component="h2" gutterBottom>
        {ORGANIZATION_LABEL}{organizationStore.selected}
      </Typography>
      
      {organizationStore.selectedOrgId && (
        <div className={classes.permissionButtons}>
        </div>
      )}
      
      {renderAliasUsersTable(aliasUsers, isLoadingAliasUsers, aliasUsersError)}

      <CreateAliasUserModal
        createModalOpen={createModalOpen}
        handleCloseCreateModal={handleCloseCreateModal}
        classes={classes}
        selectedOrganization={organizationStore.selected}
        hiMarleyUsers={hiMarleyUsers.map(user => user.email)}
        hiMarleyUsersError={hiMarleyUsersError}
        createAliasUserError={createAliasUserError}
        isLoadingHiMarleyUsers={isLoadingHiMarleyUsers}
        hasLoadedHiMarleyUsersOnce={hasLoadedHiMarleyUsersOnce}
        isSubmittingCreateUser={isLoadingCreateAliasUser}
        selectedHiMarleyUser={selectedHiMarleyUser?.email}
        handleSubmitCreateUser={handleSubmitCreateUser}
        handleDropdownSelection={handleDropdownSelection}
        onSearchHiMarleyUsers={searchHiMarleyUsers}
        hasMoreHiMarleyUsers={hiMarleyUsersHasMore}
        onLoadMoreHiMarleyUsers={loadMoreHiMarleyUsers}
      />

      <ToggleStatusModal
        isDialogOpen={isDialogOpen}
        handleCloseDialog={handleCloseDialog}
        handleConfirmStatusChange={handleConfirmStatusChange}
        loadingUpdateAliasUserStatus={isLoadingUpdateAliasUserStatus}
        updateAliasUserStatusError={updateAliasUserStatusError}
        selectedUser={selectedUser}
      />
    </div>
  )
});

export default AliasUserManagement;
